/**
 * TextAreaField — DaisyUI textarea wrapped in a FormField.
 *
 * Pure controlled component (not react-hook-form Controller).
 * Delegates label/icon/error/hint chrome to FormField.
 *
 * Usage:
 *   <TextAreaField
 *     label="Notes"
 *     icon={FileText}
 *     error={errors.notes?.message}
 *     placeholder="Notes ou instructions..."
 *     value={field.value ?? ""}
 *     onChange={(val) => field.onChange(val || "")}
 *     rows={3}
 *   />
 */

import { FormField } from "@components/forms/FormField";
import { cn } from "@lib/utils/cn";
import type { LucideIcon } from "lucide-react";
import type { TextareaHTMLAttributes } from "react";

export interface TextAreaFieldProps {
  /** Label text rendered above the textarea */
  label?: string;
  /** Lucide icon rendered next to the label */
  icon?: LucideIcon;
  /** Error message — adds textarea-error class when non-empty */
  error?: string;
  /** Hint text shown when no error is present */
  hint?: string;
  /** Placeholder text for the textarea */
  placeholder?: string;
  /** Controlled value */
  value: string;
  /** Controlled onChange handler */
  onChange: (value: string) => void;
  /** Number of rows (default: 3) */
  rows?: number;
  /** Additional CSS classes forwarded to the <textarea> element */
  className?: string;
  /** Disables the textarea when true */
  disabled?: boolean;
  /** Maximum input length — when set, renders a `{value.length}/{maxLength}` counter bottom-right */
  maxLength?: number;
}

export function TextAreaField({
  label,
  icon,
  error,
  hint,
  placeholder,
  value,
  onChange,
  rows = 3,
  className,
  disabled,
  maxLength,
}: TextAreaFieldProps) {
  const handleChange: TextareaHTMLAttributes<HTMLTextAreaElement>["onChange"] = (e) => {
    onChange(e.target.value);
  };

  const textarea = (
    <textarea
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      rows={rows}
      disabled={disabled}
      maxLength={maxLength}
      className={cn("textarea textarea-bordered w-full", error && "textarea-error", className)}
    />
  );

  return (
    <FormField label={label} icon={icon} error={error} hint={hint}>
      {maxLength !== undefined ? (
        <div className="relative">
          {textarea}
          <span className="pointer-events-none absolute right-3 bottom-2 text-xs text-[var(--ink-faint)]">
            {value.length}/{maxLength}
          </span>
        </div>
      ) : (
        textarea
      )}
    </FormField>
  );
}

export default TextAreaField;
